import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { getInstallGuideCopy } from "@/lib/pwaInstallI18n";
import { getPwaInstallInstructions, needsManualPwaInstall } from "@/lib/pwaInstall";
import { getDeferredPwaPrompt } from "@/lib/pwaInstallCapture";
import PwaInstallGuideDialog from "@/components/pwa/PwaInstallGuideDialog";

const MENU_CLOSE_MS = 350;

function InstallProgressOverlay({ progress }) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-6" dir="rtl">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
          <Download className="h-7 w-7 animate-pulse" />
        </div>
        <p className="text-center text-base font-bold text-gray-900">מתקין את האפליקציה...</p>
        <p className="mt-1 text-center text-sm text-gray-500">אשר את ההתקנה בחלון שמופיע</p>
        <Progress value={progress} className="mt-5 h-2" />
        <p className="mt-2 text-center text-xs text-emerald-700">{progress}%</p>
      </div>
    </div>,
    document.body
  );
}

/**
 * פריט תפריט — מוצג רק כשהאפליקציה לא מותקנת במכשיר.
 */
export default function PwaInstallMenuItem({ layout = "sidebar", onNavigate }) {
  const { canInstall, installing, installProgress, triggerInstall } = usePwaInstall();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!canInstall) return null;

  const instructions = getPwaInstallInstructions();
  const manualInstall = needsManualPwaInstall();
  const t = getInstallGuideCopy();
  const label = manualInstall ? t.title : "התקן אפליקציה";
  const MenuIcon = manualInstall ? Share : Download;

  const handleClick = () => {
    const nativePrompt = getDeferredPwaPrompt();
    if (nativePrompt && !manualInstall) {
      onNavigate?.();
      window.setTimeout(async () => {
        await triggerInstall();
      }, MENU_CLOSE_MS);
      return;
    }

    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) onNavigate?.();
  };

  const isSidebar = layout === "sidebar";

  return (
    <>
      {installing ? <InstallProgressOverlay progress={installProgress} /> : null}

      <Button
        type="button"
        variant="ghost"
        className={
          isSidebar
            ? "flex w-full items-center justify-start gap-3 px-2 text-emerald-700 hover:bg-emerald-50"
            : "flex w-full items-center justify-end gap-3 px-2 text-emerald-700 hover:bg-emerald-50"
        }
        onClick={handleClick}
        disabled={installing}
      >
        {isSidebar ? (
          <>
            <MenuIcon className="h-5 w-5 shrink-0" />
            <span>{label}</span>
          </>
        ) : (
          <>
            <span>{label}</span>
            <MenuIcon className="h-5 w-5 shrink-0" />
          </>
        )}
      </Button>

      <PwaInstallGuideDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        instructions={instructions}
      />
    </>
  );
}
